/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/UserExperienceSection/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
import styles from "./UserExperienceSection.module.css";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as SendIcon } from "../../assets/icons/UserExperienceSection/SendIcon.svg";
import { ReactComponent as SuccessMessageIcon } from "../../assets/icons/UserExperienceSection/SuccessMessageIcon.svg";
import { useFieldValidation } from "../../hooks/useFieldValidation";

const UserExperienceSection = () => {
  const { t } = useTranslation();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    asunto: "",
    mensaje: "",
  });

  const handleChange = (field, value) => {
    if (field === "subject") {
      setSubject(value);
    } else if (field === "message") {
      setMessage(value);
    } else {
      // Unknown field — ignore
    }
  };

  const userId = localStorage.getItem("userId");

  const handleSubmit = async () => {
    const token = localStorage.getItem("token");
    if (
      !subject.trim() ||
      !message.trim() ||
      !token ||
      token === "undefined" ||
      token === "null"
    ) {
      return;
    }

    setIsSubmitting(true);

    const authParams = {
      subject: subject,
      message: message,
      userId,
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(authParams),
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}userExperience/feedback`,
        options,
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(apiMessageOrGeneric(data?.message));
      }

      setSubmitSuccess(true);
      setSubject("");
      setMessage("");

      setTimeout(() => {
        setIsExiting(true);
      }, 2700);

      setTimeout(() => {
        setSubmitSuccess(false);
        setIsExiting(false);
      }, 3000);
    } catch (error) {
      logger.error("Error submitting feedback:", { err: sanitizeError(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {submitSuccess && (
        <div
          className={`${styles.popupOverlay} ${
            isExiting ? styles.fadeOut : ""
          }`}
        >
          <div
            className={`${styles.popupSuccessMessage} ${
              isExiting ? styles.slideUp : ""
            }`}
          >
            <SuccessMessageIcon className={styles.popupSuccessIcon} />
            <p className={styles.popupSuccessText}>
              {t("components.userExperienceSection.successMessage")}
            </p>
          </div>
        </div>
      )}

      <div className={styles.experienceContainer}>
        <div className={styles.experienceHeader}>
          <h2 className={styles.experienceTitle}>
            {t("components.userExperienceSection.title")}
          </h2>
          <p className={styles.experienceSubtitle}>
            {t("components.userExperienceSection.description")}
          </p>
        </div>

        <div className={styles.feedbackSection}>
          <div className={styles.formGroup}>
            <label htmlFor="subject" className={styles.label}>
              {t("components.userExperienceSection.subjectLabel")}
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={subject}
              onChange={(e) => {
                const value = e.target.value;
                handleChange("subject", value);
                handleFieldChange("asunto", value);
              }}
              className={`${styles.input} ${
                getFieldValidationState("asunto", subject) === "error"
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.userExperienceSection.subjectPlaceholder",
              )}
              required
              maxLength={150}
            />
            {getErrorMessage("asunto") && (
              <p className={styles.errorMessage}>{getErrorMessage("asunto")}</p>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="message" className={styles.label}>
              {t("components.userExperienceSection.messageLabel")}
            </label>
            <textarea
              id="message"
              name="message"
              value={message}
              onChange={(e) => {
                const value = e.target.value;
                handleChange("message", value);
                handleFieldChange("mensaje", value);
              }}
              className={`${styles.textarea} ${
                getFieldValidationState("mensaje", message) === "error"
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.userExperienceSection.messagePlaceholder",
              )}
              rows="6"
              required
              maxLength={300}
            />
            {getErrorMessage("mensaje") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("mensaje")}
              </p>
            )}
          </div>

          <button
            type="button"
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !subject.trim() ||
              !message.trim() ||
              !localStorage.getItem("token") ||
              hasErrors
            }
          >
            {isSubmitting ? (
              t("components.userExperienceSection.sending")
            ) : (
              <>
                <SendIcon className={styles.sendIcon} />
                {t("components.userExperienceSection.submitButton")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default UserExperienceSection;
